// EnumDataManagement.js

import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Modal from "react-modal";
import { FaTrash, FaEdit, FaPlus } from "react-icons/fa";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import './EnumDataManagement.css';
import { BASE_URL } from "../../config";
import DynamicModal from "../../components/Modal/DynamicModal";

Modal.setAppElement("#root");

const API_URL = `${BASE_URL}/api/pg-enums`;

const EnumDataManagement = ({ setOverlay }) => {
    const [enums, setEnums] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState("create");
    const [currentEnum, setCurrentEnum] = useState("");
    const [values, setValues] = useState([]);
    const [newValue, setNewValue] = useState("");
    const [newName, setNewName] = useState("");
    const [oldValue, setOldValue] = useState("");

    const fetchEnums = async () => {
        try {
            setOverlay({ active: true, message: "Učitavanje ENUM podataka..." });
            const response = await axios.get(API_URL);
            setEnums(response.data);
        } catch (error) {
            toast.error("Greška prilikom dohvata ENUM podataka.");
        } finally {
            setOverlay({ active: false });
        }
    };

    useEffect(() => {
        fetchEnums();
    }, []);

    const openModal = (enumName, currentValues, mode = "edit") => {
        setMode(mode);
        setCurrentEnum(enumName || "");
        setValues(currentValues || []);
        setNewValue("");
        setNewName("");
        setModalOpen(true);
    };

    const createEnum = async () => {
        try {
            setOverlay({ active: true, message: "Kreiranje ENUM tipa..." });
            await axios.post(API_URL + "/create", {
                name: newName,
                values: values.filter(v => v.trim() !== "")
            });
            toast.success("ENUM tip kreiran.");
            setModalOpen(false);
            await fetchEnums(); // sad je await za sigurnost
        } catch (error) {
            toast.error("Greška prilikom kreiranja ENUM tipa.");
        } finally {
            setOverlay({ active: false });
        }
    };

    const renameEnum = async (enumName) => {
        try {
            setOverlay({ active: true, message: "Preimenovanje ENUM tipa..." });

            await axios.put(API_URL + "/rename", {
                old: enumName,
                new: newName
            });

            toast.success("Naziv ENUM-a promijenjen.");
            setModalOpen(false);
            await fetchEnums();
        } catch (error) {
            toast.error("Greška prilikom preimenovanja.");
        } finally {
            setOverlay({ active: false });
        }
    };

    const addValue = async () => {
        try {
            setOverlay({ active: true, message: "Dodavanje vrijednosti..." });

            await axios.post(API_URL + "/add", {
                enum: currentEnum,
                value: newValue
            });

            toast.success("Vrijednost dodana.");
            setModalOpen(false);
            await fetchEnums();
        } catch (error) {
            toast.error("Greška prilikom dodavanja vrijednosti.");
        } finally {
            setOverlay({ active: false });
        }
    };


    const deleteValue = async (enumName, value) => {
        confirmAlert({
            message: `Obrisati vrijednost '${value}' iz ENUM-a '${enumName}'?`,
            buttons: [
                {
                    label: "Da",
                    onClick: async () => {
                        try {
                            setOverlay({ active: true, message: "Brisanje vrijednosti..." });
                            await axios.delete(API_URL + "/value", {
                                data: { enum: enumName, value }
                            });
                            toast.success("Vrijednost obrisana.");
                            await fetchEnums();
                        } catch (error) {
                            toast.error("Greška pri brisanju vrijednosti.");
                        } finally {
                            setOverlay({ active: false });
                        }
                    }
                },
                { label: "Ne" }
            ]
        });
    };

    const deleteEnum = async (enumName) => {
        confirmAlert({
            message: `Obrisati ENUM tip '${enumName}'?`,
            buttons: [
                {
                    label: "Da",
                    onClick: async () => {
                        try {
                            setOverlay({ active: true, message: "Brisanje ENUM tipa..." });
                            await axios.delete(API_URL + "/type", {
                                data: { enum: enumName }
                            });
                            toast.success("ENUM tip obrisan.");
                            await fetchEnums();
                        } catch (error) {
                            toast.error("Greška pri brisanju ENUM tipa.");
                        } finally {
                            setOverlay({ active: false });
                        }
                    }
                },
                { label: "Ne" }
            ]
        });
    };


    const updateValue = async (oldVal) => {
        try {
            await axios.put(API_URL + "/update", {
                enum: currentEnum,
                old: oldVal,
                new: newValue
            });
            toast.success("Vrijednost ažurirana.");
            setModalOpen(false);
            fetchEnums();
        } catch (error) {
            toast.error("Greška prilikom ažuriranja vrijednosti.");
        }
    };

    const renderModalContent = () => {
        switch (mode) {
            case "create":
                return (
                    <>
                        <h4>Naziv novog ENUM tipa</h4>
                        <input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Naziv ENUM-a"
                        />
                        <h4>Vrijednosti (po jedna u redu)</h4>
                        <textarea
                            rows={5}
                            value={values.join("\n")}
                            onChange={(e) => setValues(e.target.value.split("\n"))}
                        />
                    </>
                );
            case "rename":
                return (
                    <>
                        <h4>Preimenuj ENUM tip: {currentEnum}</h4>
                        <input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Novi naziv"
                        />
                    </>
                );
            case "add":
                return (
                    <>
                        <h4>Dodaj vrijednost u: {currentEnum}</h4>
                        <input
                            value={newValue}
                            onChange={(e) => setNewValue(e.target.value)}
                            placeholder="Nova vrijednost"
                        />
                    </>
                );
            case "update-value":
                return (
                    <>
                        <h4>Izmijeni vrijednost u: {currentEnum}</h4>
                        <h4 style={{ marginTop: "10px" }}>
                            Trenutna vrijednost: <strong>{oldValue}</strong>
                        </h4>

                        <input
                            value={newValue}
                            onChange={(e) => setNewValue(e.target.value)}
                            placeholder="Nova vrijednost"
                        />
                    </>
                );

            default:
                return null;
        }
    };

    const renderFooterButtons = () => {
        const commonCancelButton = (
            <button className="app-btn app-btn-secondary" onClick={() => setModalOpen(false)}>
                Otkaži
            </button>
        );

        switch (mode) {
            case "create":
                return (
                    <>
                        <button className="app-btn app-btn-success" onClick={createEnum}>Kreiraj</button>
                        {commonCancelButton}
                    </>
                );
            case "rename":
                return (
                    <>
                        <button className="app-btn app-btn-success" onClick={() => renameEnum(currentEnum)}>Preimenuj</button>
                        {commonCancelButton}
                    </>
                );
            case "add":
                return (
                    <>
                        <button className="app-btn app-btn-success" onClick={addValue}>Dodaj</button>
                        {commonCancelButton}
                    </>
                );
            case "update-value":
                return (
                    <>
                        <button className="app-btn app-btn-success" onClick={() => updateValue(oldValue)}>Ažuriraj</button>
                        {commonCancelButton}
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div>
            <h2>PostgreSQL ENUM tipovi</h2>
            <button className="create-enum-button" onClick={() => openModal(null, [], "create")}>
                <FaPlus /> Kreiraj novi ENUM
            </button>
            <table className="enum-info-table">
                <thead>
                    <tr>
                        <th>ENUM</th>
                        <th>Vrijednosti</th>
                        <th>Veza</th>
                        <th>Akcije</th>
                    </tr>
                </thead>
                <tbody>
                    {enums.map((item, index) => (
                        <tr key={index}>
                            <td>{item.enum}</td>
                            <td>
                                {item.values.map((val, i) => (
                                    <span key={i} className="enum-tag">
                                        {val}
                                        <FaEdit
                                            title="Izmijeni"
                                            className="edit-icon"
                                            onClick={() => {
                                                setCurrentEnum(item.enum);
                                                setOldValue(val);
                                                setNewValue(val);
                                                setMode("update-value");
                                                setModalOpen(true);
                                            }}
                                        />
                                        <FaTrash
                                            title="Obriši"
                                            className="trash-icon"
                                            onClick={() => deleteValue(item.enum, val)}
                                        />
                                    </span>
                                ))}
                            </td>

                            <td>
                                {item.table ? `${item.table}.${item.column}` : "❌ Nije dodijeljen"}
                            </td>
                            <td>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                                    <button
                                        title="Dodaj novu vrijednost"
                                        onClick={() => openModal(item.enum, item.values, "add")}
                                        className="btn-success"
                                    >
                                        <FaPlus />
                                    </button>
                                    <button
                                        title="Preimenuj ENUM tip"
                                        onClick={() => {
                                            setCurrentEnum(item.enum);
                                            setModalOpen(true);
                                            setMode("rename");
                                        }}
                                        className="btn-primary"
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        title="Obriši ENUM tip"
                                        className="btn-danger"
                                        onClick={() => deleteEnum(item.enum)}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            </td>

                        </tr>
                    ))}
                </tbody>
            </table>
            <DynamicModal
                isOpen={modalOpen} onRequestClose={() => setModalOpen(false)}
                title="Upravljanje tipovima podataka"
                mainContent={renderModalContent()}
                footerContent={renderFooterButtons()}
                customHeight="500px"
                customWidth="600px"
                customZIndex={1003}
            />
        </div>
    );
};

export default EnumDataManagement;
